import { useTranslation } from "react-i18next";
import type { SoftwareEdition, SoftwareProduct } from "@/pages/products/hooks/useSoftwareProducts";
import type { CatalogStatusEntry } from "@/pages/products/hooks/useProductCatalogStatus";
import DelistSuggestions from "@/pages/products/components/DelistSuggestions";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { ApplyFormValues, FieldErrors } from "@/pages/apply/hooks/useApplyForm";

interface ApplyFormProps {
  products: SoftwareProduct[];
  productsLoading: boolean;
  productsError: string | null;
  onReloadProducts: () => void;
  values: ApplyFormValues;
  errors: FieldErrors;
  onChange: (key: keyof ApplyFormValues, value: string) => void;
  submitting: boolean;
  submitError: string | null;
  onSubmit: () => void;
  selectedProduct: SoftwareProduct | null;
  archivedProduct: CatalogStatusEntry | null;
  recommendedProducts: CatalogStatusEntry[];
}

const DEFAULT_EDITIONS: SoftwareEdition[] = [
  { key: "standard", label: "standard", price: 0 },
  { key: "professional", label: "professional", price: 0 },
  { key: "enterprise", label: "enterprise", price: 0 },
];

function FieldLabel({ htmlFor, text, required }: { htmlFor: string; text: string; required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-foreground-800">
      {text}
      {required && <span className="ml-1 text-accent-600">*</span>}
    </label>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs text-accent-700">
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className="ri-error-warning-line" />
      </span>
      {message}
    </p>
  );
}

export default function ApplyForm({
  products,
  productsLoading,
  productsError,
  onReloadProducts,
  values,
  errors,
  onChange,
  submitting,
  submitError,
  onSubmit,
  selectedProduct,
  archivedProduct,
  recommendedProducts,
}: ApplyFormProps) {
  const { t } = useTranslation();
  const { edition: editionText } = useLicenseLabels();
  const editions = selectedProduct?.editions?.length ? selectedProduct.editions : DEFAULT_EDITIONS;
  const versions = selectedProduct?.versions ?? [];

  const inputClass = (hasError?: string) =>
    `w-full rounded-md border bg-background-50 px-3 py-2 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
      hasError
        ? "border-accent-400 focus:border-accent-500 focus:ring-accent-100"
        : "border-background-300 focus:border-primary-400 focus:ring-primary-100"
    }`;

  return (
    <form
      id="license-apply-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center text-primary-600">
          <i className="ri-shield-keyhole-line text-lg" />
        </span>
        <h2 className="font-heading text-base font-semibold text-foreground-950">{t("apply.infoTitle")}</h2>
      </div>

      {archivedProduct && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-700">
            <i className="ri-forbid-2-line text-lg" />
          </span>
          <div>
            <p className="text-sm font-medium text-accent-900">{t("apply.archivedTitle")}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-accent-800">
              {t("apply.archivedDesc", { name: archivedProduct.name })}
            </p>
            <DelistSuggestions
              items={recommendedProducts}
              category={archivedProduct.category}
              onPick={(slug) => onChange("productSlug", slug)}
            />
          </div>
        </div>
      )}

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel htmlFor="product" text={t("apply.product")} required />
          <select
            id="product"
            name="product"
            value={values.productSlug}
            onChange={(event) => onChange("productSlug", event.target.value)}
            disabled={productsLoading}
            className={inputClass(errors.productSlug)}
          >
            <option value="">{productsLoading ? t("apply.productLoading") : t("apply.productPlaceholder")}</option>
            {archivedProduct && (
              <option value={archivedProduct.slug} disabled>
                {archivedProduct.name}
                {t("apply.delistedSuffix")}
              </option>
            )}
            {products.map((product) => (
              <option key={product.id} value={product.slug}>
                {product.name}
              </option>
            ))}
          </select>
          <FieldError message={errors.productSlug} />
        </div>

        <div>
          <FieldLabel htmlFor="version" text={t("apply.version")} required />
          <select
            id="version"
            name="product_version"
            value={values.productVersion}
            onChange={(event) => onChange("productVersion", event.target.value)}
            disabled={!selectedProduct}
            className={inputClass(errors.productVersion)}
          >
            <option value="">{selectedProduct ? t("apply.versionPlaceholder") : t("apply.versionFirst")}</option>
            {versions.map((item) => (
              <option key={item.version} value={item.version}>
                {item.version} · {item.date}
              </option>
            ))}
          </select>
          <FieldError message={errors.productVersion} />
        </div>
      </div>

      <div className="mt-4">
        <FieldLabel htmlFor="edition-group" text={t("apply.edition")} required />
        <div id="edition-group" className="flex flex-wrap gap-2">
          {editions.map((edition) => {
            const active = values.edition === edition.key;
            return (
              <button
                key={edition.key}
                type="button"
                onClick={() => onChange("edition", edition.key)}
                className={`cursor-pointer whitespace-nowrap rounded-md border px-4 py-2 text-sm transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {editionText(edition.key)}
              </button>
            );
          })}
        </div>
        <FieldError message={errors.edition} />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel htmlFor="seats" text={t("apply.seats")} required />
          <input
            id="seats"
            name="seat_count"
            type="number"
            min={1}
            value={values.seatCount}
            onChange={(event) => onChange("seatCount", event.target.value)}
            className={inputClass(errors.seatCount)}
            placeholder={t("apply.seatsPlaceholder")}
          />
          <FieldError message={errors.seatCount} />
        </div>

        <div>
          <FieldLabel htmlFor="machine" text={t("apply.machine")} required />
          <input
            id="machine"
            name="machine_code"
            type="text"
            value={values.machineCode}
            onChange={(event) => onChange("machineCode", event.target.value)}
            className={inputClass(errors.machineCode)}
            placeholder={t("apply.machinePlaceholder")}
          />
          <p className="mt-1.5 text-xs text-foreground-500">{t("apply.machineHint")}</p>
          <FieldError message={errors.machineCode} />
        </div>
      </div>

      <div className="mt-4">
        <FieldLabel htmlFor="useCase" text={t("apply.useCase")} />
        <textarea
          id="useCase"
          name="use_case"
          rows={3}
          maxLength={500}
          value={values.useCase}
          onChange={(event) => onChange("useCase", event.target.value)}
          className={`${inputClass(errors.useCase)} resize-none`}
          placeholder={t("apply.useCasePlaceholder")}
        />
        <div className="mt-1.5 flex items-center justify-between">
          <FieldError message={errors.useCase} />
          <span className="text-xs text-foreground-400">{values.useCase.length}/500</span>
        </div>
      </div>

      <div className="mt-7 border-t border-background-200 pt-6">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center text-primary-600">
            <i className="ri-user-line text-lg" />
          </span>
          <h2 className="font-heading text-base font-semibold text-foreground-950">{t("apply.applicantTitle")}</h2>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <FieldLabel htmlFor="name" text={t("apply.name")} required />
            <input
              id="name"
              name="applicant_name"
              type="text"
              value={values.applicantName}
              onChange={(event) => onChange("applicantName", event.target.value)}
              className={inputClass(errors.applicantName)}
              placeholder={t("apply.namePlaceholder")}
            />
            <FieldError message={errors.applicantName} />
          </div>
          <div>
            <FieldLabel htmlFor="email" text={t("apply.email")} required />
            <input
              id="email"
              name="email"
              type="email"
              value={values.email}
              onChange={(event) => onChange("email", event.target.value)}
              className={inputClass(errors.email)}
              placeholder={t("apply.emailPlaceholder")}
            />
            <FieldError message={errors.email} />
          </div>
        </div>
        <div className="mt-4">
          <FieldLabel htmlFor="company" text={t("apply.company")} />
          <input
            id="company"
            name="company"
            type="text"
            value={values.company}
            onChange={(event) => onChange("company", event.target.value)}
            className={inputClass()}
            placeholder={t("apply.companyPlaceholder")}
          />
        </div>
      </div>

      {productsError && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
          <p className="text-xs text-accent-800">{productsError}</p>
          <button
            type="button"
            onClick={onReloadProducts}
            className="cursor-pointer whitespace-nowrap text-xs font-medium text-accent-800 underline"
          >
            {t("apply.reloadProducts")}
          </button>
        </div>
      )}

      {submitError && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-accent-700">
            <i className="ri-error-warning-line" />
          </span>
          <p className="text-xs text-accent-800">{submitError}</p>
        </div>
      )}

      <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-xs text-foreground-500">{t("apply.footerNote")}</p>
        <button
          type="submit"
          disabled={submitting || productsLoading || Boolean(archivedProduct)}
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {submitting ? (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
              {t("apply.submitting")}
            </>
          ) : (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-send-plane-line" />
              </span>
              {t("apply.submit")}
            </>
          )}
        </button>
      </div>
    </form>
  );
}